import React, { useEffect, useState } from "react";
import { Form, Input, message } from "antd";
import {
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useDebounce } from "use-debounce";
import {
  useRecipeQuery,
  useSaveRecipeMutation,
  useDeleteRecipeMutation,
  useRawMaterialsQuery,
  useProductsQuery,
  useSectionAccess,
} from "../../Services";
import { PAGE_SIZE, DEBOUNCED_SEARCH_QUERY } from "../../Constant";
import { PageHeader } from "../../components";
import { Product, RawMaterial, Recipe, RecipeIngredient, RawMaterialAction } from "../../Types";
import { RecipeFormDrawer, RecipesTable } from "./common";

export const RecipesPage = () => {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search] = useDebounce(searchInput, DEBOUNCED_SEARCH_QUERY);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState<Recipe | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null,
  );
  const [form] = Form.useForm();

  const [ingredients, setIngredients] = useState<RecipeIngredient[]>([]);
  const [materialSearchText, setMaterialSearchText] = useState("");
  const [productSearchText, setProductSearchText] = useState("");
  const [debouncedProductSearch] = useDebounce(
    productSearchText,
    DEBOUNCED_SEARCH_QUERY,
  );

  const { data: rawMaterialsRes } = useRawMaterialsQuery(
    { page: 1, limit: 100 },
    { staleTime: 5 * 60 * 1000 },
  );
  const allRawMaterials = rawMaterialsRes?.data || [];

  const { data: lookupProductsData } = useProductsQuery(
    { page: 1, limit: 100, search: debouncedProductSearch },
    { staleTime: 30 * 1000 },
  );
  const lookupProducts = lookupProductsData?.data || [];

  const {
    data: recipesList,
    isLoading,
    refetch,
  } = useRecipeQuery(undefined, {
    staleTime: 0,
  });
  const recipes: Recipe[] = Array.isArray(recipesList) ? recipesList : [];

  const saveMutation = useSaveRecipeMutation();
  const deleteMutation = useDeleteRecipeMutation();

  const { isAllowed } = useSectionAccess("raw_materials");

  const filteredRecipes = recipes?.filter((r) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (
      r.product_title?.toLowerCase().includes(s) ||
      r.product_sku_code?.toLowerCase().includes(s) ||
      r.product_barcode?.toLowerCase().includes(s)
    );
  });

  const paginatedRecipes = filteredRecipes.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE,
  );
  const total = filteredRecipes.length;

  useEffect(() => {
    if (drawerOpen && selectedProductId && lookupProducts.length > 0) {
      const prod = lookupProducts?.find(
        (p) => p.id === Number(selectedProductId),
      );
      if (prod && prod.stocks && prod.stocks.length > 0) {
        const currentStockId = form.getFieldValue("product_stock_id");
        if (!currentStockId) {
          const defaultStock =
            prod?.stocks?.find((s) => s.is_default === 1) || prod.stocks[0];
          form.setFieldsValue({ product_stock_id: defaultStock.id });
        }
      }
    }
  }, [lookupProducts, selectedProductId, drawerOpen, form]);

  const openDrawer = (recipe?: Recipe) => {
    form.resetFields();
    if (recipe) {
      setEditingRecipe(recipe);
      setSelectedProductId(recipe.product_id);
      setProductSearchText(recipe.product_sku_code || "");
      form.setFieldsValue({
        product_id: recipe.product_id,
        instructions: recipe.instructions,
        batch_size: recipe.batch_size ?? 1,
        additional_cost: recipe.additional_cost ?? 0,
      });

      const mapped = recipe.items.map((item) => {
        const matchingMaterial = allRawMaterials.find(
          (rm) => rm.id === item.raw_material_id,
        );
        const selectedStock = (matchingMaterial?.stocks as any[])?.find(
          (s) => s.id === item.raw_material_stock_id,
        );
        const defaultStock =
          selectedStock ||
          (matchingMaterial?.stocks as any[])?.find(
            (s) => s.is_default === 1,
          ) ||
          (matchingMaterial?.stocks as any[])?.[0];
        const defaultCost = defaultStock ? defaultStock.cost_price : 0;
        return {
          ...item,
          default_cost_price: defaultCost,
          raw_material_stock_id: item.raw_material_stock_id || defaultStock?.id,
          raw_material_batch_no:
            item.raw_material_batch_no || defaultStock?.batch_no,
        };
      });
      setIngredients(mapped);
    } else {
      setEditingRecipe(null);
      setSelectedProductId(null);
      setIngredients([]);
      form.setFieldsValue({
        product_id: undefined,
        instructions: "",
        batch_size: 1,
        additional_cost: 0,
        product_stock_id: undefined,
      });
    }
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingRecipe(null);
    setSelectedProductId(null);
    form.resetFields();
    setIngredients([]);
    setProductSearchText("");
    setMaterialSearchText("");
  };

  const selectedStockId = Form.useWatch("product_stock_id", form);

  const rawProduct =
    lookupProducts?.find((p) => p.id === Number(selectedProductId)) ||
    (editingRecipe && selectedProductId === editingRecipe.product_id
      ? {
          id: editingRecipe.product_id as any,
          title: editingRecipe.product_title,
          cost_price: editingRecipe.product_cost_price,
          retail_price: editingRecipe.product_retail_price,
          stocks: [] as any[],
        }
      : null);

  const selectedStock = (rawProduct?.stocks as any[])?.find(
    (s) => s.id === selectedStockId,
  );
  const resolvedCostPrice =
    selectedStock?.cost_price ??
    (rawProduct?.stocks as any[])?.find((s: any) => s.is_default === 1)
      ?.cost_price ??
    (rawProduct as any)?.cost_price ??
    0;
  const resolvedRetailPrice =
    selectedStock?.retail_price ??
    (rawProduct?.stocks as any[])?.find((s: any) => s.is_default === 1)
      ?.retail_price ??
    (rawProduct as any)?.retail_price ??
    0;

  const currentSelectedProduct = rawProduct
    ? {
        ...rawProduct,
        cost_price: resolvedCostPrice,
        retail_price: resolvedRetailPrice,
      }
    : null;

  const handleProductSelect = (id: string) => {
    setSelectedProductId(id);
    const prod = lookupProducts?.find((p) => p.id == id);
    const defaultStock =
      (prod?.stocks as any[])?.find((s) => s.is_default === 1) ||
      (prod?.stocks as any[])?.[0];
    form.setFieldsValue({
      product_stock_id: defaultStock ? defaultStock.id : undefined,
    });
  };

  const handleAddIngredient = (materialId: string) => {
    const mat = allRawMaterials?.find((rm) => rm.id === materialId);
    if (!mat) return;

    if (ingredients.some((itm) => itm.raw_material_id === materialId)) {
      message.warning(`"${mat.name}" is already added to the ingredients.`);
      return;
    }

    const defaultStock =
      (mat.stocks as any[])?.find((s) => s.is_default === 1) ||
      (mat.stocks as any[])?.[0];
    const defaultCost = defaultStock ? defaultStock.cost_price : 0;

    const newItem: RecipeIngredient = {
      raw_material_id: mat.id,
      raw_material_name: mat.name,
      raw_material_code: mat.code,
      raw_material_unit: mat.unit,
      quantity: 1,
      wastage_quantity: 0,
      cost: defaultCost,
      default_cost_price: defaultCost,
      raw_material_stock_id: defaultStock?.id,
      raw_material_batch_no: defaultStock?.batch_no,
    };

    setIngredients((prev) => [...prev, newItem]);
    setMaterialSearchText("");
  };

  const handleUpdateIngredient = (
    index: number,
    updates: Partial<RecipeIngredient>,
  ) => {
    setIngredients((prev) => {
      const updated = [...prev];
      if (!updated[index]) return prev;
      const itm = { ...updated[index], ...updates };

      if (
        ("quantity" in updates ||
          "wastage_quantity" in updates ||
          "default_cost_price" in updates) &&
        !("cost" in updates)
      ) {
        const defaultCost = itm.default_cost_price || 0;
        itm.cost = (itm.quantity + (itm.wastage_quantity || 0)) * defaultCost;
      }

      updated[index] = itm;
      return updated;
    });
  };

  const handleRemoveIngredient = (index: number) => {
    setIngredients((prev) => prev?.filter((_, i) => i !== index));
  };

  const handleFormSubmit = async () => {
    if (!selectedProductId) {
      message.error("Please select a product first.");
      return;
    }
    try {
      const values = await form.validateFields();
      if (ingredients.length === 0) {
        message.error("At least one ingredient is required.");
        return;
      }

      for (let i = 0; i < ingredients.length; i++) {
        const item = ingredients[i];
        if (item.quantity <= 0) {
          message.error(`Quantity in item #${i + 1} must be greater than 0.`);
          return;
        }
        if (item.wastage_quantity < 0) {
          message.error(
            `Wastage Quantity in item #${i + 1} cannot be negative.`,
          );
          return;
        }
      }

      const payload = {
        product_id: selectedProductId,
        product_stock_id: values.product_stock_id || null,
        instructions: values.instructions || "",
        batch_size: values.batch_size ?? 1,
        additional_cost: values.additional_cost ?? 0,
        items: ingredients,
      };

      const res = await saveMutation.mutateAsync(payload);
      if (res?.success) {
        message.success("Recipe saved successfully.");
        closeDrawer();
      } else {
        message.error(res?.message || "Failed to save recipe.");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteRecipe = (recipe: Recipe) => {
    deleteMutation.mutate(recipe.id, {
      onSuccess: (res) => {
        if (res?.success) {
          message.success("Recipe deleted successfully.");
        } else {
          message.error(res?.message || "Failed to delete recipe.");
        }
      },
    });
  };

  const additionalCost = Form.useWatch("additional_cost", form) || 0;
  const batchSize = Form.useWatch("batch_size", form) || 1;
  const ingredientsCost = ingredients.reduce((acc, curr) => acc + curr.cost, 0);

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <PageHeader
        title="Recipes"
        subText={`${total} recipes`}
        handleButtons={[
          ...(isAllowed(RawMaterialAction.ManageRecipes)
            ? [
                {
                  title: "Add Recipe",
                  icon: PlusOutlined,
                  onClick: () => openDrawer(),
                },
              ]
            : []),
          { title: "", icon: ReloadOutlined, onClick: refetch },
        ]}
        searchBar={
          <Input
            placeholder="Search by product name or SKU"
            prefix={<SearchOutlined />}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full sm:w-64"
            allowClear
          />
        }
      />

      <div className="mt-4 bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        <RecipesTable
          recipes={paginatedRecipes}
          total={total}
          page={page}
          isLoading={isLoading}
          onPageChange={setPage}
          onEdit={openDrawer}
          onDelete={handleDeleteRecipe}
        />
      </div>

      <RecipeFormDrawer
        open={drawerOpen}
        editingRecipe={editingRecipe}
        form={form}
        lookupProducts={lookupProducts}
        recipes={recipes}
        allRawMaterials={allRawMaterials}
        currentSelectedProduct={currentSelectedProduct}
        ingredients={ingredients}
        materialSearchText={materialSearchText}
        ingredientsCost={ingredientsCost}
        additionalCost={additionalCost}
        batchSize={batchSize}
        isSubmitting={saveMutation.isPending}
        onClose={closeDrawer}
        onSubmit={handleFormSubmit}
        onProductSearchChange={setProductSearchText}
        onProductSelect={handleProductSelect}
        onMaterialSearchChange={setMaterialSearchText}
        onAddIngredient={handleAddIngredient}
        onUpdateIngredient={handleUpdateIngredient}
        onRemoveIngredient={handleRemoveIngredient}
      />
    </div>
  );
};
